/**
 * buyer-step.js
 *
 * Handles Step 2 logic for the Invoice Generation block:
 *  - Radio button toggles (Juridinis vs Fizinis).
 *  - Company search via /company endpoint.
 *  - Population of details fields.
 *  - Locking/unlocking fields and buttons.
 */

import { downloadPdfFromBase64 } from './utils';

/**
 * @param {HTMLFormElement}        form
 * @param {import('swiper').default|null} swiper
 * @param {import('./api').default}       api
 * @param {Object}                     modal        Modal handler.
 * @param {Object}                     checkControl Reference to the receipt check controller.
 * @param {Function}                   onFinish     Callback to trigger a full reset.
 */
export function initBuyerStep(
	form,
	swiper,
	api,
	modal,
	checkControl,
	onFinish
) {
	const findBtn = form.querySelector('#ig-find-company-btn');
	const downloadBtn = form.querySelector('#ig-download-btn');
	const closeBtn = form.querySelector('#ig-close-btn');
	const statusMsg = form.querySelector('.ig-company-status-msg');

	const detailsGrid = form.querySelector('.ig-details-grid');
	const detailInputs = detailsGrid?.querySelectorAll('input') || [];

	// Inputs
	const codeInput = form.querySelector('#ig-company-code');
	const nameInput = form.querySelector('#ig-company-name');
	const addressInput = form.querySelector('#ig-company-address');
	const vatCode = form.querySelector('#ig-company-vat');
	const notes = form.querySelector('#ig-company-notes');

	const typeRadios = form.querySelectorAll('input[name="buyer_type"]');

	if (!findBtn || !swiper) return;

	/** Locked state: details fields and download btn. */
	const setLocked = (locked) => {
		detailsGrid?.classList.toggle('ig-locked', locked);
		detailInputs.forEach((input) => (input.disabled = locked));
		if (downloadBtn) downloadBtn.disabled = locked;
	};

	const setStatus = (msg, isError = false) => {
		if (!statusMsg) return;
		statusMsg.textContent = msg;
		statusMsg.classList.toggle('ig-status-msg--error', isError);
	};

	const toggleLoader = (show) => {
		const loader = form.querySelector('.ig-loader');
		loader?.classList.toggle('active', show);
		findBtn.disabled = show;
	};

	// ── Radio Toggles ────────────────────────────────────────────────────────

	const updateTypeUI = (type) => {
		const searchRow = form.querySelector('#ig-company-search-row');
		const nameLabel = form.querySelector('#ig-name-label');

		if (searchRow) {
			searchRow.classList.toggle('is-hidden', type === 'PERS');
		}

		if (nameLabel) {
			nameLabel.textContent =
				type === 'PERS'
					? nameLabel.getAttribute('data-label-pers')
					: nameLabel.getAttribute('data-label-corp');
		}

		// Clear status and fields when switching types
		setStatus('');

		// // Unlock fields for manual entry if PERS, otherwise lock and wait for search
		// if (type === 'PERS') {
		// 	setLocked(false);
		// 	// Optional: clear fields if switching to PERS?
		// 	// detailInputs.forEach(i => i.value = '');
		// 	// if (codeInput) codeInput.value = '';
		// } else {
		// 	setLocked(true);
		// }
	};

	typeRadios.forEach((radio) => {
		radio.addEventListener('change', (e) => {
			if (e.target.checked) {
				updateTypeUI(e.target.value);
			}
		});
	});

	// Initial UI state
	const checkedRadio = form.querySelector('input[name="buyer_type"]:checked');
	if (checkedRadio) updateTypeUI(checkedRadio.value);

	// ── Company Search ───────────────────────────────────────────────────────

	const handleFindCompany = async () => {
		const receipts = checkControl.getReceipts();
		if (!receipts || receipts.length === 0) {
			setStatus(
				window.igTranslations?.error_no_receipts || 'Kvitų nėra.',
				true
			);
			return;
		}

		// Ignore if PERS - should be hidden anyway, but for "security"
		const activeType = form.querySelector(
			'input[name="buyer_type"]:checked'
		)?.value;
		if (activeType === 'PERS') return;

		const code = codeInput?.value.trim();

		if (!code) {
			setStatus(
				window.igTranslations?.error_fields_required ||
					'Užpildykite visus laukus.',
				true
			);
			return;
		}

		setStatus('');
		setLocked(true);

		try {
			toggleLoader(true);

			const response = await api.fetchCompany(code);
			const body = response.body ?? response;

			if (body.status === 0 && body.data) {
				const d = body.data;

				// Populate fields
				const nameEl = form.querySelector('#ig-company-name');
				const addressEl = form.querySelector('#ig-company-address');
				const vatEl = form.querySelector('#ig-company-vat');

				if (nameEl) nameEl.value = d.name || '';
				if (addressEl) addressEl.value = d.address || '';
				if (vatEl) vatEl.value = d.vatcode || '';
				if (codeInput) codeInput.value = d.code || code; // Ensure code is updated if it was partial

				setStatus('');
			} else {
				setStatus(response.message || 'Duomenys nerasti.', true);
			}
		} catch (err) {
			console.error('[IG Buyer] Search error:', err);
			setStatus(
				window.igTranslations?.error_generic || 'Įvyko klaida.',
				true
			);
		} finally {
			toggleLoader(false);
			setLocked(false);
		}
	};

	findBtn.addEventListener('click', handleFindCompany);

	// Enter key triggers search
	codeInput?.addEventListener('keydown', (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleFindCompany();
		}
	});

	/**
	 * Validate mandatory fields based on active buyer type.
	 * @returns {boolean}
	 */
	const validateStep2 = () => {
		const activeType = form.querySelector(
			'input[name="buyer_type"]:checked'
		)?.value;
		const name = form.querySelector('#ig-company-name')?.value.trim();
		const address = form.querySelector('#ig-company-address')?.value.trim();
		const code = codeInput?.value.trim();

		setStatus('');

		let isValid = true;
		if (activeType === 'PERS') {
			if (!name || !address) isValid = false;
		} else {
			// LT or INT
			if (!name || !address || !code) isValid = false;
		}

		if (!isValid) {
			setStatus(
				window.igTranslations?.error_fields_required ||
					'Užpildykite visus laukus.',
				true
			);
		}

		return isValid;
	};

	/**
	 * Fetch and download the PDF
	 */
	const downloadInvoice = async () => {
		const receipts = checkControl.getReceipts();
		console.log('Receipts:', receipts);

		if (!receipts || receipts.length === 0) {
			setStatus(
				window.igTranslations?.error_no_receipts || 'Kvitų nėra.',
				true
			);
			return;
		}

		const activeType = form.querySelector(
			'input[name="buyer_type"]:checked'
		)?.value;
		const code = codeInput?.value.trim() ?? '';
		const name = nameInput?.value.trim() ?? '';
		const address = addressInput?.value.trim() ?? '';
		const vat = vatCode?.value.trim() ?? '';
		const additionalNotes = notes?.value.trim() ?? '';

		if (!name || !address) {
			setStatus(
				window.igTranslations?.error_fields_required ||
					'Užpildykite visus laukus.',
				true
			);
			return;
		}

		const buyerData = {
			type: activeType,
			code: code,
			name: name,
			address: address,
			vatcode: vat,
			remark: additionalNotes,
		};

		setStatus('');

		try {
			toggleLoader(true);
			if (downloadBtn) downloadBtn.disabled = true;

			const response = await api.generateInvoice(
				receipts.map((r) => r.raw || r),
				buyerData
			);
			const body = response.body ?? response;

			if (body.status === 0) {
				// Step 2: Generate PDF from invoice data
				const pdfResponse = await api.generateInvoicePdf(body.data);

				if (pdfResponse.status === 0 && pdfResponse.data) {
					// Step 3: Trigger Success Modal and handle download via its button
					modal.open(
						'tmpl-ig-invoice-success',
						pdfResponse.data || {}
					);

					// Handle Base64 PDF download via utility
					downloadPdfFromBase64(pdfResponse.data);

					setStatus('');

					// Trigger central reset after successful download
					setTimeout(() => {
						if (typeof onFinish === 'function') onFinish();
					}, 500);
				} else {
					// Status 101 or other error
					setStatus(
						pdfResponse.message ||
							window.igTranslations?.error_pdf ||
							'Validacijos klaida (101).',
						true
					);
				}
			} else {
				setStatus(
					body.message ||
						window.igTranslations?.error_generic ||
						'Nepavyko sugeneruoti sąskaitos.',
					true
				);
			}
		} catch (err) {
			console.error('[IG Buyer] Download error:', err);
			setStatus(
				window.igTranslations?.error_generic || 'Įvyko klaida.',
				true
			);
		} finally {
			toggleLoader(false);
			if (downloadBtn) downloadBtn.disabled = false;
		}
	};

	// ── Actions ──────────────────────────────────────────────────────────────

	closeBtn?.addEventListener('click', () => {
		if (typeof onFinish === 'function') onFinish();
	});

	downloadBtn?.addEventListener('click', () => {
		if (validateStep2()) {
			downloadInvoice();
		}
	});

	return {
		reset: () => {
			if (codeInput) codeInput.value = '';
			detailInputs.forEach((input) => (input.value = ''));
			setStatus('');

			const firstRadio = form.querySelector(
				'input[name="buyer_type"][value="LT"]'
			);
			if (firstRadio) {
				firstRadio.checked = true;
				updateTypeUI('LT');
			}
		},
	};
}
